<template>
	<slot
		name="default"
		v-bind="{
			loadingProfiIeImage,
			hasProfileImage,
			profileImage,
			person: matchedPerson,
			team: matchedTeam,
			initials,
		}"
	>
		<v-avatar
			align-self="center"
			color="grey-lighten-4"
			:rounded="rounded ?? undefined"
			:size="avatarSize"
			v-bind="$attrs"
		>
			<BaseImage :src="profileImage" cover>
				<template #placeholder="{ centerClasses }">
					<div :class="centerClasses">
						<v-progress-circular
							color="primary"
							indeterminate
							:size="loadingSize"
						/>
					</div>
				</template>
				<template
					v-if="showAliasOnError || $slots.error"
					#error="{ centerClasses }"
				>
					<div :class="centerClasses">
						<slot name="error">
							<span class="text-caption font-bold">
								{{ initials }}
							</span>
						</slot>
					</div>
				</template>
			</BaseImage>
		</v-avatar>
	</slot>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { useProfileImage } from 'composables/useProfileImage.ts'
import BaseImage from '../../BaseImage.vue'

interface Props {
	user?: Api.User | Api.WhoAmI | null
	person?: Api.Person
	team?: Api.Team
	showAliasOnError: boolean
	loadingSize?: number
	avatarSize?: number
	rounded?: string | number | null
}

const {
	user = null,
	person,
	team,
	loadingSize = 50,
	avatarSize = 100,
	rounded = 1,
	showAliasOnError = true,
} = defineProps<Props>()

const { loadProfileImage, hasProfileImage, profileImage, loadingProfiIeImage } =
	useProfileImage()

const matchedPerson = computed(() => user?.persons?.[0] ?? person)
const matchedTeam = computed(() =>
	typeof user?.team !== 'undefined' ? user.team : team
)

const initials = computed(() => {
	const person = matchedPerson.value
	const team = matchedTeam.value

	if (typeof person !== 'undefined') {
		if (
			typeof person.firstname === 'string' &&
			typeof person.firstname.length > 0
		) {
			return person.firstname.charAt(0) + person.lastname.charAt(0)
		}
		return person.lastname.slice(0, 2).toUpperCase()
	}
	if (team && (team.first_name || team.last_name)) {
		return [team.first_name, team.last_name]
			.filter(Boolean)
			.map((item) => item.charAt(0))
			.join('')
	}
	return ''
})

watch(
	() => [user?.id, user?.profile_image],
	async () => {
		await loadProfileImage(user)
	},
	{ immediate: true }
)
</script>

<style scoped></style>
